import * as React from 'react';
import { useState } from 'react';

import { SlSkeleton } from '@shoelace-style/shoelace/dist/react';
import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { useQueryClient } from '@tanstack/react-query';
import { bulkQuestions } from '~api/question';
import { QuizLimit } from '~api/quiz';
import { personalQuizUrl } from '~api/urls';
import { useAtomValue } from 'jotai';

import {
  SaveGeneratedQuestionsEventAdapter,
  SaveGeneratedQuestionsEventSource,
} from '~analytics/adapters/SaveGeneratedQuestionsEventAdapter';
import { PaywallScreens } from '~analytics/types';
import { translate } from '~l10n/client';
import { languageAtom } from '~shared/atoms/languageAtom';
import { SmallButton } from '~shared/components/buttons/SmallButton';
import { toast } from '~shared/components/Toast';
import { RateQuestionQuality } from '~teacher/components/QuizEditor/GenerateQuestionDialog/RateQuestionQuality';
import { RateQuestionQualityAiDetails } from '~teacher/components/QuizEditor/GenerateQuestionDialog/RateQuestionQualityFeedbackDialog';
import { useOpenQuizLimitPaywall } from '~teacher/components/QuizzesPaywall';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useUpdateQuiz } from '~teacher/hook';
import { useBulkCreateQuestions } from '~teacher/hook/question/useBulkCreateQuestions';
import { useCreateQuizMutation } from '~teacher/hook/useCreateQuizMutation';
import { quizBySocNumberQueryOptions } from '~teacher/hook/useQuiz';

import { countBy } from '../../../../../../../../../shared/src/utils/countBy';
import { GenerateQuestionDialogContent } from '../Layout/GenerateQuestionDialogContent';
import { SelectQuestionCheckbox } from './SelectQuestionCheckbox';

interface SelectQuestionsProps extends QuizLimit {
  onBack: () => void;
  onClose: () => void;
  quizName: string;
  questions: Array<Question>;
  source: SaveGeneratedQuestionsEventSource;
  aiDetails?: RateQuestionQualityAiDetails;
}

type Question = {
  id: string;
  questionType: QuestionTypes;
  text: string;
  explanation?: string;
  answers?: Array<{
    order: number;
    text: string;
    isCorrect: boolean;
  }>;
};

function useSelectedQuestions() {
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<Set<string>>(
    new Set()
  );

  const handleQuestionCheckedChanged = ({
    addOrRemove,
    questionId,
  }: {
    addOrRemove: 'add' | 'delete';
    questionId: string;
  }) => {
    const newSelectedQuestionIds = new Set([...selectedQuestionIds]);
    switch (addOrRemove) {
      case 'add':
        newSelectedQuestionIds.add(questionId);
        break;
      case 'delete':
        newSelectedQuestionIds.delete(questionId);
        break;
      default:
        break;
    }

    setSelectedQuestionIds(newSelectedQuestionIds);
  };

  return {
    selectedQuestionIds,
    setSelectedQuestionIds,
    handleQuestionCheckedChanged,
  };
}
export function SelectQuestions({
  onBack,
  onClose,
  questions,
  quizName,
  currentQuizCount,
  limit,
  source,
  aiDetails,
}: SelectQuestionsProps) {
  const openQuizLimitPaywall = useOpenQuizLimitPaywall();
  const { selectedQuestionIds, handleQuestionCheckedChanged } =
    useSelectedQuestions();

  const router = useSocrativeRouter();

  const { handleSave, isSaving } = useHandleCreateQuiz();

  const selectedQuestions = [...selectedQuestionIds]
    .map((questionId) =>
      questions.find((question) => question.id === questionId)
    )
    .filter((question): question is Question => Boolean(question));

  const {
    MC: numMc = 0,
    TF: numTf = 0,
    FR: numSa = 0,
  } = countBy({
    items: selectedQuestions,
    iteratee: (question: { questionType?: QuestionTypes }) =>
      question.questionType,
  });

  return (
    <GenerateQuestionDialogContent
      header={
        <GenerateQuestionDialogContent.Header
          onBack={onBack}
          description={translate('Choose questions to save to your library.')}
          title={translate('Generate Questions')}
        />
      }
      footer={
        <GenerateQuestionDialogContent.Footer>
          <div className="text-center text-sans text-xs leading-5 text-blue-600 p-4">
            {translate(
              'Generated questions can make mistakes. Consider checking question accuracy.'
            )}
          </div>
          <div
            className="flex justify-end gap-2 p-5"
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
          >
            <SmallButton
              isDisabled={isSaving || selectedQuestionIds.size === 0}
              onClick={async () => {
                if (currentQuizCount >= limit) {
                  await openQuizLimitPaywall({
                    paywallTouch: [PaywallScreens.QuizListLimitReached],
                  });
                  return;
                }

                const { socNumber, updatedQuiz } = await handleSave({
                  quizName,
                  selectedQuestions,
                });

                new SaveGeneratedQuestionsEventAdapter({
                  numQuestionsGenerated: questions.length,
                  numQuestionsSelected: selectedQuestionIds.size,
                  source: source,
                  saveAction: 'save_edit',
                  numTf,
                  numMc,
                  numSa,
                }).sendAnalytics();

                router.routeToEditQuiz(socNumber, updatedQuiz.version);
              }}
            >
              {translate('Save & Edit')}
            </SmallButton>
            <SmallButton
              isDisabled={isSaving || selectedQuestionIds.size === 0}
              onClick={async () => {
                if (currentQuizCount >= limit) {
                  await openQuizLimitPaywall({
                    paywallTouch: [PaywallScreens.QuizListLimitReached],
                  });
                  return;
                }

                const { socNumber, updatedQuiz } = await handleSave({
                  quizName,
                  selectedQuestions,
                });

                new SaveGeneratedQuestionsEventAdapter({
                  numQuestionsGenerated: questions.length,
                  numQuestionsSelected: selectedQuestionIds.size,
                  source: source,
                  numTf,
                  numMc,
                  numSa,
                  saveAction: 'save',
                }).sendAnalytics();

                const message = (toastInstance: any) => {
                  const handleClick = (e: React.MouseEvent) => {
                    e.preventDefault();
                    toast.dismiss(toastInstance.id);
                    router.routeToEditQuiz(socNumber, updatedQuiz.version);
                  };

                  return (
                    <div className="flex items-center gap-2">
                      <span>
                        {translate('{0} saved to Personal library.').format(
                          updatedQuiz.name
                        )}
                      </span>
                      <a
                        href={`#edit-quiz/${socNumber}`}
                        onClick={handleClick}
                        className="text-sm text-white leading-6 font-medium underline hover:no-underline cursor-pointer"
                      >
                        {translate('View')}
                      </a>
                    </div>
                  );
                };

                toast.positive(message, {
                  duration: 5000,
                });

                onClose();
              }}
            >
              {translate('Save')}
            </SmallButton>
          </div>
        </GenerateQuestionDialogContent.Footer>
      }
    >
      <div className="flex flex-col gap-2">
        {questions.map(({ id, questionType, text, answers, explanation }) => (
          <SelectQuestionCheckbox
            key={id}
            id={id}
            questionType={questionType}
            text={text}
            answers={answers}
            explanation={explanation}
            checked={selectedQuestionIds.has(id)}
            onChange={(checked) =>
              handleQuestionCheckedChanged({
                questionId: id,
                addOrRemove: checked ? 'add' : 'delete',
              })
            }
          />
        ))}
        <RateQuestionQuality
          source={
            source === 'areas_of_improvement'
              ? 'areas_of_improvement'
              : 'specific_students'
          }
          aiDetails={aiDetails}
        />
      </div>
    </GenerateQuestionDialogContent>
  );
}

interface SelectQuestionsLoadingProps {
  onBack: () => void;
  questionCount: number;
}

export function SelectQuestionsLoading({
  onBack,
  questionCount,
}: SelectQuestionsLoadingProps) {
  return (
    <GenerateQuestionDialogContent
      header={
        <GenerateQuestionDialogContent.Header
          onBack={onBack}
          description={translate(
            'Select Areas of Improvement to include in the assessment.'
          )}
          title={translate('Generate Questions')}
        />
      }
      footer={
        <GenerateQuestionDialogContent.Footer>
          <div className="text-center text-sans text-xs leading-5 text-blue-600 p-4">
            {translate(
              'Generated questions can make mistakes. Consider checking question accuracy.'
            )}
          </div>
          <div
            className="flex justify-end gap-2 p-5"
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
          >
            <SmallButton isDisabled onClick={() => {}}>
              {translate('Save & Edit')}
            </SmallButton>
            <SmallButton isDisabled onClick={() => {}}>
              {translate('Save')}
            </SmallButton>
          </div>
        </GenerateQuestionDialogContent.Footer>
      }
    >
      <div className="flex flex-col gap-2">
        <div className="question-skeletons">
          {[...Array(questionCount)].map((_, index) => (
            <SlSkeleton effect="pulse" key={index} />
          ))}
        </div>
      </div>
    </GenerateQuestionDialogContent>
  );
}

function useHandleCreateQuiz() {
  const language = useAtomValue(languageAtom);
  const [isSaving, setIsSaving] = useState(false);
  const createQuizMutation = useCreateQuizMutation();
  const updateQuizMutation = useUpdateQuiz();
  const bulkCreateQuestions = useBulkCreateQuestions({ url: bulkQuestions });

  const queryClient = useQueryClient();

  const handleCreateQuiz = async ({
    quizName,
    selectedQuestions,
  }: {
    quizName: string;
    selectedQuestions: Array<Question>;
  }) => {
    setIsSaving(true);
    try {
      // Create quiz
      const { socNumber } = await createQuizMutation.mutateAsync({
        parentId: null,
      });

      // (and then fetch it since creating a quiz only returns the SOC code
      const {
        quiz: { id: quizId, sharable },
      } = await queryClient.fetchQuery(
        quizBySocNumberQueryOptions({
          socNumber,
          quizUrl: personalQuizUrl,
          queryClient,
        })
      );

      const newQuizName = translate('{0} – {1} (Generated)').format(
        quizName,
        new Date().toLocaleString(language, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      );

      // Update quiz name
      const updatedQuiz = await updateQuizMutation.mutateAsync({
        quizId,
        name: newQuizName,
        isSharable: sharable,
        quizUrl: personalQuizUrl,
      });

      // Bulk add questions
      const questionsToAdd = [...selectedQuestions].map(
        ({ questionType, text, answers, explanation }) => ({
          type: questionType,
          questionText: text,
          answers:
            answers?.map(({ order, text, isCorrect }) => ({
              order,
              text,
              isCorrect,
            })) ?? [],
          explanation: explanation ?? '',
          gradingWeight: 1,
        })
      );

      await bulkCreateQuestions.mutateAsync({
        quizId: String(quizId),
        questions: questionsToAdd,
        insertionOrder: 1,
      });
      return { socNumber, updatedQuiz };
    } catch (error) {
      console.error(error);
      toast.critical(translate('Something went wrong'));
      throw error;
    } finally {
      setIsSaving(false);
    }
  };
  return { handleSave: handleCreateQuiz, isSaving };
}
